import React from 'react';
import PropTypes from 'prop-types';
import { HOC } from 'formsy-react';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import naturalCompare from 'natural-compare-lite';
import NestedSelectorChips from './NestedSelectorChips';
import Spinner from '../Spinner/Spinner';
import { dropDownStyle, common } from '../../../theme/AllocateBaseTheme';
import _ from '../../../utils/lodash';
import { lowerFirstLetter, removeSpaces } from '../../../utils/helpers';
import globals from '../../../globals/communityGlobals';

const styles = {
  nestedSelector: { fontSize: '13px' },
  choiceComponent: { width: '100%' },
};

class NestedSelector extends React.Component {
  state = { searchText: '', isDirty: false, callArgs: [], selectedMenuItems: [] };
  componentDidMount() {
    const { isFetchRequired, getOptions } = this.props;
    if (isFetchRequired) {
      getOptions();
    }
  }

  componentWillReceiveProps = nextProps => {
    const { originalValue, autoComplete, isFetchRequired, getOptions } = nextProps;
    if (autoComplete && originalValue) {
      this.setState({ searchText: '' });
    }

    if (
      isFetchRequired &&
      (this.state.callArgs.length !== nextProps.callArgs.length ||
        Object.values(this.props.callArgs).join(',') !== Object.values(nextProps.callArgs).join(','))
    ) {
      this.setState({ callArgs: nextProps.callArgs });
      getOptions();
    }
  };

  componentDidUpdate = () => {
    const { isDirty } = this.state;
    const { options, getValue, isFetching, setValue, value: propsValue, multiple } = this.props;

    const value = isDirty ? getValue() : propsValue;
    if (!isFetching && options && value) {
      if (multiple) {
        if (!value.every(v => options.some(o => o.id === v.id))) {
          setValue(null);
        }
      } else if (!options.some(o => o.id === value.id)) {
        setValue(null);
      }
    }
  };

  onChipChange = (id, event, key, newValue) => {
    const { getValue, setValue } = this.props;
    setValue(getValue().map(v => (v.id === id ? { ...v, innerId: newValue } : v)));
    this.setState({ isDirty: true });
  };

  menuItemStyle = menuItem => {
    return {
      fontSize: '14px',
      backgroundColor: this.state.selectedMenuItems.findIndex(x => x.id === menuItem) !== -1 ? 'lightgrey' : 'white',
    };
  };

  changeBuffer = (event, key, indexes) => {
    const ids = indexes.map(i => i.id);
    const unique = ids.filter(v => ids.indexOf(v) === ids.lastIndexOf(v));
    const uniqueValues = indexes.filter(i => unique.indexOf(i.id) !== -1);
    const { setValue, options, onChange } = this.props;
    setValue(uniqueValues);
    this.setState({ isDirty: true, selectedMenuItems: uniqueValues });
    const option = options && options.find(o => o.id === uniqueValues);
    onChange(event, key, uniqueValues, option);
  };

  handleRequestDelete = key => () => {
    const { setValue, getValue, options, onChange } = this.props;
    const newValue = getValue().slice();
    newValue.splice(newValue.indexOf(newValue.find(o => o.id === key)), 1);
    setValue(newValue.length ? newValue : undefined);
    this.setState({ isDirty: true, selectedMenuItems: newValue });
    const option = options && options.find(o => o.id === newValue);
    onChange(event, key, newValue, option);
  };

  handleUpdateInput = searchText => {
    this.setState({
      searchText,
    });
  };

  selectionRender = () => {
    const { getValue } = this.props;
    const value = getValue();
    const name = (_.isEmpty(value) ? [] : value).map(v => v.name).join(', ');
    return name;
  };

  handleNewRequest = item => {
    const { setValue, getValue, multiple } = this.props;
    let searchText = '';
    if (multiple) {
      const oldValue = getValue() || [];
      setValue([...oldValue, item]);
    } else {
      searchText = item.text;
      setValue(item);
    }

    this.setState({ searchText, isDirty: true });
  };

  generateItems = () => {
    const { isDirty } = this.state;
    const {
      autoComplete,
      reduxIdentifier,
      options,
      value: propsValue,
      getValue,
      multiple,
      filterOptions,
      mapOptions,
      localeArea,
      shouldCompareNatural,
    } = this.props;

    const locale = localeArea ? globals.getArea(localeArea) : {};
    const value = isDirty ? getValue() : propsValue;
    let optionsCopy = options ? mapOptions(filterOptions(options)).slice() : [];
    optionsCopy = shouldCompareNatural ? optionsCopy.sort((a, b) => naturalCompare(a.name, b.name)) : optionsCopy;
    const itemTemplateGenerator = ({ id, name, ragStatus, band, orgUnitsIds, rootId }) => (
      <MenuItem
        style={this.menuItemStyle(id)}
        key={`selectOpt_${reduxIdentifier + id + removeSpaces(name)}`}
        value={{ id, name, innerId: 0, ragStatus, band, orgUnitsIds, rootId }}
        primaryText={localeArea ? locale.getMsg(lowerFirstLetter(name)) : name}
      />
    );

    if (autoComplete) {
      const autoCompleteOptionsPreFilter =
        value && value.length && multiple ? _.remove(optionsCopy, ({ id }) => !value.includes(id)) : optionsCopy;
      return autoCompleteOptionsPreFilter.map(el => ({
        id: el.id,
        text: el.name,
        value: itemTemplateGenerator(el),
      }));
    }
    return optionsCopy.map(el => itemTemplateGenerator(el));
  };

  render() {
    const { isDirty } = this.state;
    const {
      isFetching,
      options,
      getValue,
      disabled,
      setValidations,
      setValue,
      resetValue,
      hasValue,
      getErrorMessage,
      getErrorMessages,
      isFormDisabled,
      isValid,
      isPristine,
      isFormSubmitted,
      isRequired,
      showRequired,
      showError,
      isValidValue,
      endpoint,
      reduxIdentifier,
      isFetchRequired,
      getOptions,
      validationErrors,
      originalValue,
      value: propsValue,
      autoComplete,
      validationError,
      multiple,
      innerRef,
      onChange,
      clearable,
      validations,
      filterOptions,
      mapOptions,
      localeArea,
      callArgs,
      mapValue,
      ...restOfProps
    } = this.props;

    const itemMap = this.generateItems();
    const value = isDirty ? getValue() : propsValue;
    const isSpinner = isFetching || !options;
    const choiceComponent = (
      <SelectField
        className="select-field-comp"
        value={value}
        onChange={this.changeBuffer}
        disabled={disabled}
        multiple={multiple}
        selectionRenderer={this.selectionRender}
        fullWidth
        floatingLabelStyle={common.floatingLabelStyle}
        floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
        floatingLabelFocusStyle={common.floatingLabelFocusStyle}
        underlineStyle={common.underlineStyle}
        style={dropDownStyle.style}
        iconStyle={dropDownStyle.iconStyle}
        labelStyle={dropDownStyle.labelStyle}
        menuStyle={dropDownStyle.menuStyle}
        maxHeight={200}
        inputStyle={dropDownStyle.inputStyle}
        dropDownMenuProps={{
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
            maxHeight: 150,
          },
        }}
        {...restOfProps}
      >
        {itemMap}
      </SelectField>
    );
    return isSpinner ? (
      <Spinner />
    ) : (
      <div style={styles.choiceComponent}>
        {choiceComponent}
        {Array.isArray(value) && options && options.length && value && (
          <NestedSelectorChips
            style={styles.nestedSelector}
            options={mapOptions(options)}
            value={value}
            handleRequestDelete={this.handleRequestDelete}
            disabled={disabled}
            localeArea={localeArea}
            onChipChange={(id, event, key, newValue) => this.onChipChange(id, event, key, newValue)}
          />
        )}
      </div>
    );
  }
}

NestedSelector.defaultProps = {
  required: false,
  value: undefined,
  originalValue: undefined,
  options: null,
  multiple: false,
  clearable: false,
  isFetchRequired: false,
  disabled: false,
  autoComplete: false,
  localeArea: null,
  validationErrors: {},
  validationError: null,
  validations: {},
  innerRef: () => {},
  onChange: () => {},
  filterOptions: options => options,
  mapOptions: options => options,
  mapValue: value => value,
  callArgs: [],
  shouldCompareNatural: true,
};

const optionPropType = PropTypes.shape({
  id: PropTypes.number,
  value: PropTypes.number,
  name: PropTypes.string,
});

NestedSelector.propTypes = {
  floatingLabelText: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionPropType),
  innerRef: PropTypes.func,
  // className: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  value: PropTypes.oneOfType([
    // Used by formsy
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        innerId: PropTypes.number,
      }),
    ),
  ]),
  originalValue: PropTypes.oneOfType([
    // Used by formsy
    PropTypes.arrayOf(optionPropType),
    optionPropType,
  ]),
  isFetching: PropTypes.bool.isRequired,
  multiple: PropTypes.bool,
  clearable: PropTypes.bool,
  isFetchRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.bool,
  localeArea: PropTypes.string,
  reduxIdentifier: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  mapValue: PropTypes.func,
  // Formsy props:
  setValidations: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValue: PropTypes.func.isRequired,
  getOptions: PropTypes.func.isRequired,
  resetValue: PropTypes.func.isRequired,
  hasValue: PropTypes.func.isRequired,
  getErrorMessage: PropTypes.func.isRequired,
  getErrorMessages: PropTypes.func.isRequired,
  isFormDisabled: PropTypes.func.isRequired,
  isValid: PropTypes.func.isRequired,
  isPristine: PropTypes.func.isRequired,
  isFormSubmitted: PropTypes.func.isRequired,
  isRequired: PropTypes.func.isRequired,
  showRequired: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
  isValidValue: PropTypes.func.isRequired,
  // Other propTypes throw a 'marked as required' error for isRequired inside validationErrors ?!
  validationErrors: PropTypes.shape(),
  validationError: PropTypes.string,
  filterOptions: PropTypes.func,
  mapOptions: PropTypes.func,
  validations: PropTypes.shape(),
  // eslint-disable-next-line react/forbid-prop-types
  callArgs: PropTypes.any,
  shouldCompareNatural: PropTypes.bool,
};

export default HOC(NestedSelector);
